/* eslint-disable object-curly-newline */

import React, { useRef, useReducer, useEffect } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import { Link, Flex, Button, Box } from 'components/ui';
import mediaUtils from 'utils/Media';
import Modal from 'components/Modal/Modal';
import Dropdown from 'components/Dropdown/Dropdown';
import { modal, space } from 'style/config';
import languageLocale from 'utils/LanguageLocale';
import FilterEN from 'components/ProductPage/Filters/Filter/locales/Filter_en_US';
import FilterFR from 'components/ProductPage/Filters/Filter/locales/Filter_fr_CA';

const { Media } = mediaUtils;
const getText = languageLocale.getLocalisedDataFromResource(FilterEN, FilterFR);

const convertArrayToAvailablePropObject = (arr = []) => {
    return arr.reduce((acc, item) => {
        acc[item] = true;

        return acc;
    }, {});
};

const getAvailableProps = obj => {
    const props = Object.keys(obj);

    return props.filter(prop => obj[prop]);
};

const FILTER_INITIAL = { isOpen: false, isModalOpen: false, selected: {} };

function filterReducer(state, action) {
    switch (action.type) {
        case 'SET_SELECTED':
            return { ...state, selected: action.selected };
        case 'TOGGLE_DROPDOWN':
            return { ...state, isOpen: action.isOpen, selected: action.selected };
        case 'TOGGLE_MODAL':
            return { ...state, isOpen: !state.isOpen, isModalOpen: !state.isModalOpen, selected: action.selected };
        default:
            return state;
    }
}

// eslint-disable-next-line complexity
const Filter = ({
    title,
    id,
    dropDownDataAt,
    sortBtnDataAt,
    isSingleSelection,
    trigger,
    content,
    width = 244,
    align = 'left',
    hasCustomScroll,
    selected: selectedProp,
    bpFilterActive,
    isRedesigned,
    applyFilters: applyFiltersProp,
    name
}) => {
    const [state, dispatch] = useReducer(filterReducer, undefined, () => ({
        ...FILTER_INITIAL,
        selected: convertArrayToAvailablePropObject(selectedProp)
    }));
    const dropdownRef = useRef(null);
    const isFilterOpenRef = useRef(false);
    isFilterOpenRef.current = state.isOpen || state.isModalOpen;
    const prevSelectedPropRef = useRef(selectedProp);

    useEffect(() => {
        if (prevSelectedPropRef.current !== selectedProp && !isFilterOpenRef.current) {
            dispatch({ type: 'SET_SELECTED', selected: convertArrayToAvailablePropObject(selectedProp) });
        }

        prevSelectedPropRef.current = selectedProp;
    }, [selectedProp]);

    const resetSelected = () => convertArrayToAvailablePropObject(selectedProp);

    const closeModalDropdown = e => {
        const resetedSelected = resetSelected();

        if (state.isModalOpen) {
            dispatch({ type: 'TOGGLE_MODAL', selected: resetedSelected });
        } else {
            dispatch({ type: 'SET_SELECTED', selected: resetedSelected });
            dropdownRef.current?.triggerDropdown(e, false);
        }
    };

    const applyFilters = selectedState => {
        const filtersToApply = getAvailableProps(selectedState);
        applyFiltersProp({ [name]: filtersToApply });
        closeModalDropdown();
    };

    const onItemToggled = value => {
        if (isSingleSelection) {
            const newSelected = { [value]: true };
            dispatch({ type: 'SET_SELECTED', selected: newSelected });
            applyFilters(newSelected);
        } else {
            dispatch({ type: 'SET_SELECTED', selected: { ...state.selected, [value]: !state.selected[value] } });
        }
    };

    const isItemSelected = value => state.selected[value];

    const toggleDropdown = (e, isDropdownOpen) => {
        dispatch({ type: 'TOGGLE_DROPDOWN', isOpen: isDropdownOpen, selected: resetSelected() });
    };

    const toggleModal = e => {
        e?.preventDefault();
        dispatch({ type: 'TOGGLE_MODAL', selected: resetSelected() });
    };

    const getFooterButtons = isDropdown => (
        <Flex
            alignItems='center'
            justifyContent='space-between'
        >
            <Link
                is='span'
                color='blue'
                data-at={Sephora.debug.dataAt('clear_btn')}
                padding={2}
                margin={-2}
                onClick={() => dispatch({ type: 'SET_SELECTED', selected: {} })}
            >
                {getText('clear')}
            </Link>
            <Button
                variant='primary'
                data-at={Sephora.debug.dataAt('done_btn')}
                {...(isDropdown ? { size: 'sm', minWidth: '10.5em' } : { hasMinWidth: true })}
                onClick={() => applyFilters(state.selected)}
            >
                {getText('done')}
            </Button>
        </Flex>
    );

    const { isOpen, isModalOpen, selected } = state;
    const tempFilters = getAvailableProps(selected);
    const isActive = ((tempFilters && tempFilters.length > 0) || (selectedProp && selectedProp.length > 0) || isOpen) && !bpFilterActive;

    return (
        <React.Fragment>
            <Media greaterThan='xs'>
                <Dropdown
                    id={id}
                    data-at={Sephora.debug.dataAt('filter_block')}
                    ref={dropdownRef}
                    closeOnClick={false}
                    useClick={true}
                    onTrigger={(e, isDropdownOpen) => toggleDropdown(e, isDropdownOpen)}
                    hasCustomScroll={hasCustomScroll}
                >
                    <Dropdown.Trigger
                        aria-label={title}
                        data-at={Sephora.debug.dataAt(sortBtnDataAt)}
                        css={{ outline: 0 }}
                    >
                        {trigger({ isActive, title, isSmall: false })}
                    </Dropdown.Trigger>
                    <Dropdown.Menu
                        data-at={Sephora.debug.dataAt(dropDownDataAt)}
                        align={align}
                        offset={2}
                        width={isRedesigned ? 375 : width}
                        {...(!isRedesigned && {
                            paddingTop: 3,
                            paddingBottom: isSingleSelection && 3
                        })}
                    >
                        {isRedesigned ? (
                            <Box css={styles.optionsContainer}>{content({ onClick: onItemToggled, isSelected: isItemSelected })}</Box>
                        ) : (
                            content({ onClick: onItemToggled, isSelected: isItemSelected })
                        )}

                        {isSingleSelection || (
                            <Box
                                marginTop={3}
                                boxShadow='light'
                                paddingY={2}
                                paddingX={4}
                                children={getFooterButtons(true)}
                            />
                        )}
                    </Dropdown.Menu>
                </Dropdown>
            </Media>

            <Media at='xs'>
                <div onClick={toggleModal}>{trigger({ isActive, title, isSmall: true })}</div>
                {isModalOpen ? (
                    <Modal
                        isOpen={true}
                        isDrawer={true}
                        onDismiss={toggleModal}
                    >
                        <Modal.Header>
                            <Modal.Title children={title} />
                        </Modal.Header>
                        <Modal.Body
                            paddingBottom={[isSingleSelection ? modal.paddingLg : modal.paddingSm]}
                            paddingX={null}
                            {...(isRedesigned && { css: styles.mobileOptionsContainer })}
                        >
                            {content({ onClick: onItemToggled, isSelected: isItemSelected, isModal: true })}
                        </Modal.Body>
                        {isSingleSelection || <Modal.Footer children={getFooterButtons()} />}
                    </Modal>
                ) : null}
            </Media>
        </React.Fragment>
    );
};

const styles = {
    optionsContainer: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        paddingTop: space[5],
        paddingLeft: space[4],
        paddingRight: space[4],
        paddingBottom: '20px',
        gap: `${space[3]}px 15px`
    },
    mobileOptionsContainer: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        paddingTop: space[4],
        paddingLeft: space[4],
        paddingRight: space[4],
        paddingBottom: space[6],
        gap: `${space[2]}px ${space[3]}px`
    }
};

export default wrapFunctionalComponent(Filter, 'Filter');
